import { type ChatAction, type ChatState } from "@/types"

export const initialState: ChatState = {
  messages: [],
  isLoading: false,
  error: null,
  threadId: null,
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case "ADD_MESSAGE":
      return {
        ...state,
        messages: [...state.messages, action.payload],
        error: null,
      }

    case "SET_MESSAGE_VOTE": {
      const { id, wasHelpful } = action.payload
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === id && msg.type === "text" ? { ...msg, wasHelpful } : msg,
        ),
      }
    }

    case "SET_MESSAGE_SUGGESTIONS": {
      const { serverId, chips } = action.payload
      const hasMatch = state.messages.some(
        (msg) => msg.type === "text" && msg.serverId === serverId,
      )
      if (hasMatch) {
        return {
          ...state,
          messages: state.messages.map((msg) =>
            msg.type === "text" && msg.serverId === serverId
              ? { ...msg, suggestions: chips }
              : msg,
          ),
        }
      }

      // No serverId match: fallback chips for a failed turn attach to the
      // trailing error bubble, which never gets a serverId (error streams
      // never emit `complete`).
      const lastMessage = state.messages[state.messages.length - 1]
      if (lastMessage?.type === "error") {
        return {
          ...state,
          messages: [
            ...state.messages.slice(0, -1),
            { ...lastMessage, suggestions: chips },
          ],
        }
      }
      return state
    }

    case "UPSERT_STREAMING_MESSAGE": {
      const { id, segments, streamed } = action.payload
      const exists = state.messages.some((msg) => msg.id === id)
      if (exists) {
        return {
          ...state,
          messages: state.messages.map((msg) =>
            msg.id === id && msg.type === "text"
              // Once `streamed` latches true (a text_delta arrived), keep it true
              // for the rest of the turn — a later text/widget event must not
              // reset the streaming-governor signal.
              ? { ...msg, segments, streamed: streamed || msg.streamed }
              : msg,
          ),
        }
      }
      return {
        ...state,
        messages: [
          ...state.messages,
          {
            id,
            sender: "agent",
            type: "text",
            segments,
            timestamp: Date.now(),
            streamed,
          },
        ],
        error: null,
      }
    }

    case "FINALIZE_STREAMING_MESSAGE": {
      const { id, serverId } = action.payload
      return {
        ...state,
        messages: state.messages.map((msg) =>
          msg.id === id && msg.type === "text"
            ? { ...msg, serverId }
            : msg,
        ),
      }
    }

    case "REMOVE_MESSAGE": {
      const { id } = action.payload
      return {
        ...state,
        messages: state.messages.filter((msg) => msg.id !== id),
      }
    }

    case "SET_LOADING":
      return {
        ...state,
        isLoading: action.payload,
      }

    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        isLoading: false,
      }

    case "CLEAR_MESSAGES":
      return {
        ...state,
        messages: [],
        error: null,
        threadId: null,
      }

    case "SET_THREAD_ID":
      return {
        ...state,
        threadId: action.payload,
      }

    default:
      return state
  }
}
